import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {Row, Col, Card} from 'react-bootstrap';
import _ from "lodash";
import Swal from 'sweetalert2';
import { Provider } from 'react-redux';
import { Room, HKReportStatus, HKRoomType, HKRoomState, HKCleanType, HKReport, HKReportBase, HKAuditMarkingReport, HKClean, AuditStates } from '../../../store/models/domain';
import RoomStateComponent from "../../components/room/RoomStateComponentV2"
import { authProvider } from "../../../common/auth/AuthProvider";
import { SwalForm } from '../../components/Alert';
import ResidentService from '../../../services/ResidentService';

import "./housekeeping.scss"
import ColorUtils from '../../../utils/ColorUtils';
import moment from 'moment';

interface Props extends WithTranslation {
    room: Room
    roomTypes: HKRoomType[]
    hkReports: HKReport[]
    resident: any
    roomSchedule: any
    flagState?: string
    auditStates?: AuditStates
    onClose(): void
    onNewReport(room: Room): void
    onAuditReport(reportId: string): void
    onAuditMarking(room: Room): void
    store?: any //Sweetforms renders above Provider so it must be re-wrapped
}


const RoomHistoryForm = (props: Props) => {
    const {
        room,
        roomTypes,
        hkReports,
        resident,
        roomSchedule,
        flagState,
        auditStates,
        onClose,
        onNewReport,
        onAuditReport,
        onAuditMarking,
        
        //Store
        store,
        // Translate props
        t,
    } = props;
    
    const cleans = React.useMemo(() => {
        return _.orderBy(room.housekeepingHistory ?? [], c => c.dateComplete).reverse();
    }, [room.housekeepingHistory])
    
    const currentClean = _.first(cleans); // latest cleaning report
    const currentCleanType = room?.stateCleanType ?? currentClean?.cleanType ?? roomSchedule?.cleanType?.cleanType

    const report = React.useMemo(() => {
        return hkReports.find((report: HKReport) => report.id === currentClean?.reportId)
    }, [hkReports])

    // check if has room states
    const roomStates = React.useMemo(() => {
        const roomType = roomTypes.find((t: HKRoomType) => t.roomType === room.roomType) as HKRoomType
        const states =  !roomType?.roomStates ? [] : roomType!.roomStates!.filter(s => !!s.stateName && s.tags?.length > 0)
        const cleanTypes = roomType?.routines?.map((item) => item.cleanType)
        // use clean types
        return !states.length ? cleanTypes : states
    }, [roomTypes])

    // get clean type
    const roomType = roomTypes.find((type) => type.roomType === room.roomType)
    let currentRoomRoutine = roomType?.routines?.find((r) => r.cleanType === currentCleanType)
    
    //get the room routines for the color definition of the button
    const reportRoutines = _.first(report?.roomType.routines)
    const typeColor = currentRoomRoutine?.color?? reportRoutines?.color?? ''

    const goToReportsPage = (roomId: string) => {
        window.location.href = `/reporting/reports?roomId=${roomId}`
    };

    const showRoomStateModal = () => {
        if (roomStates.length === 0) return
        return SwalForm.fire({
            html: (
            <Provider store={store}>
                <RoomStateComponent
                    room={room}
                    roomTypes={roomTypes}
                    cleanType={currentCleanType}
                    center
                    onClose={() => { Swal.close() }}
                    store={store}
                    />
            </Provider>
            ),
            width: '50vw'
        });
    }
    return (
        <Provider store={store}>
            <div className="roomHistoryForm">
                {!currentCleanType ?
                    <Row className="justify-content-md-center">
                        <Col sm={12} md={12} className="px-3">                                            
                            <h3>
                                {t('No cleaning checklist available.')}
                            </h3>
                        </Col> 
                    </Row>
                :   
                    <>
                        <Row className="justify-content-md-center">
                            <Col sm={12} md={12} className="px-3">                                            
                                <Card className="room-history-form-card">
                                    <Card.Header className='text-left ml-3'>
                                        {t('Room Status')}
                                    </Card.Header>      
                                    <Card.Body>
                                        <div className="swal2-actions d-flex flex-row justify-content-between px-3" style={{'fontSize': '16px'}}>                                
                                            <div className="button-default truncate-text-width">{!resident?.firstName ? t("Unoccupied") : ResidentService.displayName(resident) }</div>
                                            <div className="button-default pointer" role="button" style={{ backgroundColor: typeColor, color: typeColor ? ColorUtils.getContrastColor(typeColor) : "black" }} onClick={() => onNewReport(room)}>{currentCleanType}</div>
                                            <button className={`swal2-styled button-default  ${authProvider.hasRole(authProvider.Roles.FacilityAssistant) ? 'secondary': 'visible-none'}`} type="button" onClick={() => goToReportsPage(room.id)}>{t('Open Cleaning Report')}</button>
                                        </div>
                                    </Card.Body>                 
                                </Card>
                            </Col> 
                        </Row>

                        <Row className="justify-content-md-center">
                            <Col sm={12} md={12} className="px-3">                                            
                                <Card className="room-history-form-card">
                                    <Card.Header className='text-left ml-3'>
                                        {t('Room Audit')}
                                    </Card.Header>      
                                    <Card.Body>
                                        {/*
                                            Slot 1: Observation
                                            Slot 2: Simple Marking + journey states
                                            Slot 3: Detailed Marking (future feature — empty for now)
                                        */}
                                        <div className="swal2-actions d-flex flex-row justify-content-between px-3">
                                            {/* SLOT 1: Observation
                                                Always rendered. Disabled (greyed) when the marking
                                                journey is active — prevents accidentally destroying
                                                an in-flight marking by clicking obs. Visible rather
                                                than hidden so the supervisor sees why it's locked.
                                            */}
                                            <div className="audit-slot">
                                                {auditStates?.isMarkingJourneyActive ? (
                                                    <button
                                                        className="button-default adaptive disabled"
                                                        type="button"
                                                        disabled
                                                        aria-label={t('Observation locked while marking journey active')}
                                                    >
                                                        {t('Observation')}
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="swal2-styled button-default adaptive"
                                                        type="button"
                                                        onClick={() => onAuditReport(cleans?.[0]?.reportId)}
                                                    >
                                                        {t('Observation')}
                                                    </button>
                                                )}
                                            </div>

                                            {/* SLOT 2: Simple Marking + journey states
                                                Transforms through the marking journey:
                                                  Idle             → "Simple Marking" (enabled)
                                                  Pre-Audit Marked → "Marked - waiting on cleaning" (disabled)
                                                  Audit Ready      → "Complete the Audit" (enabled)
                                                  Audit Complete   → returns to "Simple Marking" (enabled)
                                                                     fresh restart available after completion
                                            */}
                                            <div className="audit-slot">
                                                {auditStates?.isPreAuditMarked && (
                                                    <button
                                                        className="button-default adaptive disabled"
                                                        type="button"
                                                        disabled
                                                    >
                                                        {t('Marked - waiting on cleaning')}
                                                    </button>
                                                )}
                                                {auditStates?.isMarkingAuditReady && (
                                                    <button
                                                        className="swal2-styled button-default adaptive"
                                                        type="button"
                                                        onClick={() => onAuditReport(cleans[0].reportId)}
                                                    >
                                                        {t('Complete the Audit')}
                                                    </button>
                                                )}
                                                {!auditStates?.isMarkingJourneyActive && (
                                                    <button
                                                        className="swal2-styled button-default adaptive"
                                                        type="button"
                                                        onClick={() => onAuditMarking(room)}
                                                    >
                                                        {t('Simple Marking')}
                                                    </button>
                                                )}
                                            </div>

                                                {/* SLOT 3: Detailed Marking
                                                    Future feature. Slot is reserved — never fill with
                                                    a button from another journey, even temporarily.
                                                    Always renders an invisible placeholder so the slot
                                                    holds position.
                                                */}
                                                <div className="audit-slot">
                                                    <button
                                                        className="button-default secondary visible-none"
                                                        type="button"
                                                        aria-hidden="true"
                                                        tabIndex={-1}
                                                    >
                                                        {t('Detailed Marking')}
                                                    </button>
                                                </div>
                                            
                                        </div>
                                        
                                    </Card.Body>                 
                                </Card>
                            </Col> 
                        </Row>
                        {authProvider.hasRole(authProvider.Roles.FacilityAssistant) &&
                            <Row className="justify-content-md-center">
                                <Col sm={12} md={12} className="px-3">                                            
                                    <Card className="room-history-form-card">
                                        <Card.Header className='text-left ml-3'>
                                            {t('Room Management')}
                                        </Card.Header>      
                                        <Card.Body>
                                            <div className="swal2-actions d-flex flex-row justify-content-between px-3">                                
                                                <button className="swal2-styled button-default adaptive" disabled={roomStates && roomStates.length===0} type="button" onClick={() => showRoomStateModal()}>{t('Change Cleaning Type')}</button>
                                                <button className="swal2-styled button-default visible-none" type="button">{t('View/Edit Cleaning Schedule')}</button>
                                                <button className="button-default secondary disabled" disabled>{t('Create Room QR Code')}</button>
                                            </div>
                                        </Card.Body>                 
                                    </Card>
                                </Col> 
                            </Row>
                        }
                    </>
                }
                <Row>
                    <Col sm={12} md={12} className="px-3">                    
                        <div className="swal2-actions d-flex flex-row justify-content-start px-3">
                            <button className="button-default swal2-cancel swal2-styled" type="button" onClick={() => { onClose() }}>{t("Cancel")}</button>                              
                        </div>
                    </Col>
                </Row>
            </div>
        </Provider>
    );
};
    
const translate = withTranslation();
export default translate(RoomHistoryForm);
